import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { docsApi, docResummarize, docReprocess, docStatus, mattersApi, pleadingCheckApi } from "@/lib/api";
import { useAsyncJob } from "@/lib/useAsyncJob";
import { JobProgress } from "@/components/StrategyPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ProgressTimeline } from "@/components/ProgressTimeline";
import VoiceButton from "@/components/VoiceButton";
import { SourceChip } from "@/components/SourceChip";
import { ArrowLeft, Chat, Clock, Translate, FileText, DownloadSimple, Sparkle, ShieldWarning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function DocumentDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [data, setData] = useState(null);
    const [query, setQuery] = useState("");
    const [answer, setAnswer] = useState(null);
    const [loadingA, setLoadingA] = useState(false);
    const [chronology, setChronology] = useState(null);
    const [loadingC, setLoadingC] = useState(false);
    const [translation, setTranslation] = useState(null);
    const [loadingT, setLoadingT] = useState(false);
    const [resummarizing, setResummarizing] = useState(false);
    const [reprocessing, setReprocessing] = useState(false);
    const [matters, setMatters] = useState([]);

    useEffect(() => {
        mattersApi.list().then(setMatters).catch(() => {});
    }, []);

    const linkToMatter = async (matterId) => {
        try {
            const updated = await docsApi.update(id, { matter_id: matterId || null });
            setData((prev) => prev ? { ...prev, document: { ...prev.document, matter_id: updated.matter_id } } : prev);
            const m = matters.find((x) => x.id === matterId);
            toast.success(matterId ? `Linked to "${m?.title || "matter"}"` : "Unlinked from matter");
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Failed to update document");
        }
    };

    const resummarize = async () => {
        setResummarizing(true);
        try {
            const res = await docResummarize(id);
            setData((prev) => prev ? { ...prev, document: { ...prev.document, summary: res.summary } } : prev);
            toast.success("Summary generated");
            await load();
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Could not generate summary");
        }
        setResummarizing(false);
    };

    const reprocess = async () => {
        if (!window.confirm("Re-run OCR + extraction on this document? This may take 30-60 seconds for large PDFs.")) return;
        setReprocessing(true);
        try {
            const res = await docReprocess(id);
            toast.success("Document reprocessed");
            await load();
        } catch (e) {
            console.error(e);
            toast.error(e?.response?.data?.detail || "Reprocess failed");
        }
        setReprocessing(false);
    };

    const load = useCallback(async () => {
        try {
            setData(await docsApi.get(id));
        } catch {
            toast.error("Failed to load document");
        }
    }, [id]);

    useEffect(() => {
        load();
    }, [load]);

    // Poll status if processing
    const pollRef = useRef(null);
    useEffect(() => {
        const status = data?.document?.processing_status;
        if (!status || (status !== "processing" && status !== "queued")) return;
        pollRef.current = setInterval(async () => {
            try {
                const s = await docStatus(id);
                if (s.processing_status === "ready" || s.processing_status === "error") {
                    clearInterval(pollRef.current);
                    await load();
                } else {
                    setData((prev) => prev ? { ...prev, document: { ...prev.document, ...s } } : prev);
                }
            } catch {}
        }, 3000);
        return () => clearInterval(pollRef.current);
    }, [data?.document?.processing_status, id, load]);

    const ask = async () => {
        if (!query.trim()) return;
        setLoadingA(true);
        setAnswer(null);
        try {
            setAnswer(await docsApi.ask(id, query));
        } catch (e) {
            console.error(e);
            toast.error("Query failed");
        }
        setLoadingA(false);
    };

    const buildChronology = async () => {
        setLoadingC(true);
        try {
            setChronology(await docsApi.chronology([id]));
        } catch {
            toast.error("Chronology failed");
        }
        setLoadingC(false);
    };

    const translate = async (target) => {
        setLoadingT(true);
        try {
            const preview = data?.document?.extracted_text_preview || "";
            if (!preview) {
                toast.error("No extracted text yet");
                setLoadingT(false);
                return;
            }
            const t = await docsApi.translate(preview.slice(0, 3000), target);
            setTranslation({ target, text: t.translation });
        } catch {
            toast.error("Translate failed");
        }
        setLoadingT(false);
    };

    if (!data) return <div className="p-8 text-sm text-muted-foreground">Loading…</div>;
    const d = data.document;
    const docStages = buildDocStages(d, !!resummarizing);

    return (
        <div className="h-[calc(100vh-57px)] flex flex-col" data-testid="document-detail">
            <div className="px-4 md:px-8 py-4 border-b border-border bg-white/70">
                <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="mb-2 -ml-2"><ArrowLeft size={14} /> Back</Button>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{d.doc_type || "document"}</div>
                        <h1 className="font-serif text-2xl md:text-3xl tracking-tight truncate">{d.original_filename}</h1>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                            <span className="chip chip-doc">{d.language}</span>
                            {d.used_ocr && <span className="chip chip-ai">OCR</span>}
                            {d.processing_status === "queued" && <span className="chip chip-ai" data-testid="status-queued">Queued · async OCR</span>}
                            {d.processing_status === "processing" && (
                                <span className="chip chip-ai" data-testid="status-processing">
                                    Processing
                                    {d.ocr_total_pages ? ` · page ${d.ocr_pages_done || 0} / ${d.ocr_total_pages}` : "…"}
                                </span>
                            )}
                            {d.processing_status === "error" && <span className="chip chip-doc text-destructive border-destructive/40" data-testid="status-error">Processing failed</span>}
                            <span className="text-xs text-muted-foreground">{d.num_pages} pages · {(d.size / 1024).toFixed(1)} KB</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Select
                            value={d.matter_id || "none"}
                            onValueChange={(val) => linkToMatter(val === "none" ? null : val)}
                        >
                            <SelectTrigger className="h-8 text-xs w-[220px]" data-testid="doc-matter-select">
                                <SelectValue placeholder="Link to matter…" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">— Unlinked —</SelectItem>
                                {matters.map((m) => (
                                    <SelectItem key={m.id} value={m.id}>{m.title}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={reprocess}
                            disabled={reprocessing}
                            data-testid="reprocess-btn"
                            title="Re-run OCR & extraction"
                        >
                            {reprocessing ? "Reprocessing…" : "↻ Reprocess"}
                        </Button>
                        <a href={docsApi.downloadUrl(d.id)} className="inline-flex items-center gap-1 text-xs border border-border rounded-sm px-3 h-8" target="_blank" rel="noreferrer" data-testid="doc-download-btn"><DownloadSimple size={13} /> Original</a>
                    </div>
                </div>
            </div>

            <div className="grid grid-cols-12 flex-1 min-h-0 max-w-[1600px] w-full mx-auto overflow-y-auto lg:overflow-visible">
                {/* left: pages / chunks */}
                <aside className="col-span-12 lg:col-span-4 border-r border-border bg-white/60 overflow-y-auto scrollbar-slim p-4" data-testid="doc-left">
                    {(d.processing_status !== "ready" || resummarizing) && (
                        <div className="mb-4">
                            <ProgressTimeline
                                stages={docStages}
                                title="Document pipeline"
                                subtitle={d.processing_status === "ready" ? "Generating summary…" : undefined}
                                data-testid="doc-progress"
                            />
                        </div>
                    )}
                    <div className="flex items-center justify-between mb-2">
                        <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Summary</div>
                        {d.processing_status === "ready" && (
                            <button
                                onClick={resummarize}
                                disabled={resummarizing}
                                data-testid="resummarize-btn"
                                className="text-[10px] uppercase tracking-[0.2em] text-primary hover:underline disabled:opacity-50"
                            >
                                {resummarizing ? "Generating…" : (d.summary ? "↻ Regenerate" : "↻ Generate")}
                            </button>
                        )}
                    </div>
                    <div className="border border-border rounded-sm bg-white p-3 text-sm leading-relaxed mb-4 min-h-[60px]" data-testid="document-summary">
                        {d.processing_status === "queued" || d.processing_status === "processing" ? (
                            <span className="text-muted-foreground inline-flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
                                Generating summary…
                            </span>
                        ) : d.processing_status === "error" ? (
                            <span className="text-destructive">Processing failed. {d.error || ""}</span>
                        ) : d.summary ? (
                            d.summary
                        ) : (
                            <div>
                                <div className="text-muted-foreground">Summary unavailable.</div>
                                <button
                                    onClick={resummarize}
                                    disabled={resummarizing}
                                    className="mt-2 text-xs text-primary underline disabled:opacity-50"
                                    data-testid="retry-summary-btn"
                                >
                                    {resummarizing ? "Generating…" : "Generate summary now"}
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Pages</div>
                    {!data.chunks.length ? (
                        <div className="text-xs text-muted-foreground">No extracted content.</div>
                    ) : (
                        <div className="space-y-2">
                            {groupByPage(data.chunks).map(([page, chunks]) => (
                                <details key={page} className="border border-border rounded-sm bg-white">
                                    <summary className="cursor-pointer px-3 py-2 text-sm font-medium">Page {page}</summary>
                                    <div className="p-3 text-[13px] whitespace-pre-wrap leading-relaxed border-t border-border max-h-80 overflow-y-auto scrollbar-slim">
                                        {chunks.map((c) => c.text).join("\n\n")}
                                    </div>
                                </details>
                            ))}
                        </div>
                    )}
                </aside>

                {/* right: chat + actions */}
                <section className="col-span-12 lg:col-span-8 flex flex-col overflow-hidden">
                    <Tabs defaultValue="chat" className="flex-1 min-h-0 flex flex-col">
                        <TabsList className="mx-4 mt-3 self-start">
                            <TabsTrigger value="chat" data-testid="tab-doc-chat"><Chat size={14} className="mr-1" /> Chat</TabsTrigger>
                            <TabsTrigger value="chronology" data-testid="tab-doc-chrono" onClick={buildChronology}><Clock size={14} className="mr-1" /> Chronology</TabsTrigger>
                            <TabsTrigger value="translate" data-testid="tab-doc-translate"><Translate size={14} className="mr-1" /> Translate</TabsTrigger>
                            <TabsTrigger value="redflag" data-testid="tab-doc-redflag"><ShieldWarning size={14} className="mr-1" /> Red flags</TabsTrigger>
                        </TabsList>

                        <TabsContent value="chat" className="flex-1 min-h-0 flex flex-col p-4 data-[state=inactive]:hidden">
                            <div className="text-xs text-muted-foreground mb-2 flex items-center gap-2">
                                <SourceChip type="internal">Document-based analysis</SourceChip>
                                Answers cite page numbers from this document only.
                            </div>
                            <div className="flex-1 min-h-0 overflow-y-auto scrollbar-slim border border-border rounded-sm bg-white p-4" style={{ maxHeight: "calc(100vh - 320px)" }}>
                                {!answer ? (
                                    <div className="text-sm text-muted-foreground">
                                        Try: <i>"Summarise in Hindi"</i>, <i>"What did the court hold on maintainability?"</i>, <i>"List all the dates mentioned"</i>.
                                    </div>
                                ) : (
                                    <div className="fade-in">
                                        <div className="text-xs mb-2 flex flex-wrap gap-1">
                                            {[...new Set(answer.pages_used || [])].map((p) => (
                                                <span key={p} className="chip chip-doc">Source: page {p}</span>
                                            ))}
                                        </div>
                                        <div className="whitespace-pre-wrap text-[15px] leading-relaxed" data-testid="doc-chat-answer">{answer.answer}</div>
                                    </div>
                                )}
                            </div>
                            <div className="pt-3 flex items-center gap-2 shrink-0 badge-safe-row badge-safe-bottom">
                                <Input
                                    placeholder="Ask anything about this document…"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && ask()}
                                    data-testid="doc-ask-input"
                                />
                                <VoiceButton compact onTranscript={({ text }) => setQuery(text)} testid="doc-ask-mic" />
                                <Button onClick={ask} disabled={loadingA} data-testid="doc-ask-btn">{loadingA ? "…" : "Ask"}</Button>
                            </div>
                        </TabsContent>

                        <TabsContent value="chronology" className="flex-1 min-h-0 overflow-y-auto p-4">
                            <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
                                <SourceChip type="internal">Extracted from document</SourceChip>
                            </div>
                            {loadingC ? (
                                <div className="text-sm text-muted-foreground">Building chronology…</div>
                            ) : !chronology?.entries?.length ? (
                                <div className="text-sm text-muted-foreground">No dated events found. Click "Chronology" tab to rebuild.</div>
                            ) : (
                                <ol className="relative border-l border-border ml-2 space-y-3">
                                    {chronology.entries.map((e, i) => (
                                        <li key={i} className="pl-4 relative">
                                            <span className="absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-primary" />
                                            <div className="text-xs font-mono text-primary">{e.date}</div>
                                            <div className="text-sm">{e.event}</div>
                                            {e.source && (
                                                <div className="text-[11px] text-muted-foreground">
                                                    {e.source.document} · p{e.source.page}
                                                </div>
                                            )}
                                        </li>
                                    ))}
                                </ol>
                            )}
                        </TabsContent>

                        <TabsContent value="translate" className="flex-1 min-h-0 overflow-y-auto p-4">
                            <div className="flex items-center gap-2 mb-3">
                                <Button variant="outline" size="sm" onClick={() => translate("en")} disabled={loadingT}>Translate → English</Button>
                                <Button variant="outline" size="sm" onClick={() => translate("hi")} disabled={loadingT}>Translate → हिन्दी</Button>
                            </div>
                            {loadingT ? (
                                <div className="text-sm text-muted-foreground">Translating preview…</div>
                            ) : translation ? (
                                <div className="border border-border rounded-sm p-4 bg-white whitespace-pre-wrap font-serif text-[15px] leading-relaxed">
                                    {translation.text}
                                </div>
                            ) : (
                                <div className="text-sm text-muted-foreground">Translates first 3000 characters of extracted text.</div>
                            )}
                        </TabsContent>
                    </Tabs>
                </section>
            </div>
        </div>
    );
}

function groupByPage(chunks) {
    const map = new Map();
    for (const c of chunks) {
        if (!map.has(c.page)) map.set(c.page, []);
        map.get(c.page).push(c);
    }
    return Array.from(map.entries()).sort((a, b) => a[0] - b[0]);
}

// Map the document's `processing_status` + summary state into a 3-stage
// timeline. Used while a doc is being processed asynchronously so the user
// can see what the backend is doing instead of just a single chip.
function buildDocStages(doc, summarizing) {
    const status = doc.processing_status;
    const upload = { key: "upload", label: "Uploaded", status: "completed" };
    let extract = { key: "extract", label: "Extracting text & running OCR" };
    let summary = { key: "summary", label: "Generating summary" };
    if (status === "queued") {
        extract.status = "queued";
        extract.reasoning = "Waiting for the OCR worker to pick this up…";
        summary.status = "queued";
    } else if (status === "processing") {
        extract.status = "processing";
        extract.reasoning =
            doc.used_ocr === undefined
                ? "Running pdf2image + Tesseract (Hindi + English)…"
                : "Building chunks for retrieval…";
        summary.status = "queued";
    } else if (status === "error") {
        extract.status = "failed";
        extract.reasoning = doc.processing_error || "Extraction failed. Try Reprocess.";
        summary.status = "queued";
    } else {
        // ready
        extract.status = "completed";
        extract.reasoning = `${doc.num_pages} page(s), ${doc.used_ocr ? "OCR used" : "native text"}.`;
        if (summarizing) {
            summary.status = "processing";
            summary.reasoning = "Senior model summarising this document…";
        } else if (doc.summary) {
            summary.status = "completed";
            summary.reasoning = "Summary ready in the left panel.";
        } else {
            summary.status = "queued";
            summary.reasoning = "No summary yet — click ↻ Generate in the Summary panel.";
        }
    }
    return [upload, extract, summary];
}

function ContractRedflagPanel({ documentId }) {
    const [report, setReport] = useState(null);
    const { job, busy, error, start, reset } = useAsyncJob();
    const [representing, setRepresenting] = useState("our_side");
    const [contractType, setContractType] = useState("commercial");

    const run = async () => {
        const j = await start(() => pleadingCheckApi.contractRedflag({
            document_id: documentId, representing, contract_type: contractType,
        }));
        if (j?.status === "completed") {
            setReport(j.result);
            toast.success(`${j.result?.data?.red_flags?.length || 0} red flag(s)`);
        } else if (j?.status === "failed") {
            toast.error(error || j.error || "Scan failed");
        }
    };

    const sevColor = {
        critical: "border-destructive/50 bg-destructive/10",
        high: "border-destructive/40 bg-destructive/5",
        medium: "border-amber-300 bg-amber-50/40",
        low: "border-emerald-300 bg-emerald-50/40",
    };
    const flags = report?.data?.red_flags || [];
    const missing = report?.data?.missing_clauses || [];
    const score = report?.data?.overall_score;

    return (
        <div data-testid="redflag-panel">
            <div className="text-xs text-muted-foreground mb-3">
                AI scans this document for indemnity, jurisdiction, IP, payment, MSMED-Act, RERA and other contract risks from your perspective.
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Representing</div>
                    <Select value={representing} onValueChange={setRepresenting}>
                        <SelectTrigger data-testid="redflag-representing"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="our_side">Our client (flag unfavourable terms)</SelectItem>
                            <SelectItem value="the_other_side">Opposing party (anticipate their view)</SelectItem>
                            <SelectItem value="neutral_advisor">Neutral advisor</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <div>
                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">Contract type</div>
                    <Input value={contractType} onChange={(e) => setContractType(e.target.value)} placeholder="e.g. SPA, MSA, lease" data-testid="redflag-ctype" />
                </div>
                <div className="flex items-end justify-end">
                    <Button onClick={run} disabled={busy} data-testid="redflag-run-btn">
                        <ShieldWarning size={14} /> {busy ? "Scanning…" : "Scan now"}
                    </Button>
                </div>
            </div>

            {busy && job && (
                <div className="border border-border bg-white rounded-md p-3 mb-3" data-testid="redflag-progress">
                    <div className="text-xs text-muted-foreground">Scanning contract clauses…</div>
                    <JobProgress job={job} />
                </div>
            )}
            {error && !busy && <div className="text-xs text-destructive mb-3">{error} <button onClick={reset} className="underline ml-1">retry</button></div>}

            {report && (
                <div className="space-y-3" data-testid="redflag-report">
                    {typeof score === "number" && (
                        <div className="flex items-center gap-3 border border-border rounded-md p-3 bg-white">
                            <div className="text-3xl font-serif">{score}</div>
                            <div className="flex-1">
                                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Overall risk · {report.data?.overall_risk}</div>
                                <div className="text-xs italic text-muted-foreground">{report.data?.summary}</div>
                            </div>
                        </div>
                    )}
                    {!flags.length && !missing.length ? (
                        <div className="text-sm text-emerald-700">No red flags detected.</div>
                    ) : (
                        <>
                            {flags.length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Red flags</div>
                                    {flags.map((f, i) => (
                                        <div key={i} className={`border rounded-sm p-3 ${sevColor[f.severity] || "border-border"}`} data-testid={`redflag-${i}`}>
                                            <div className="flex items-center gap-2 flex-wrap text-xs">
                                                <span className="chip chip-doc">{f.severity}</span>
                                                <span className="font-medium uppercase tracking-[0.15em] text-muted-foreground">{f.category}</span>
                                            </div>
                                            {f.quote && <div className="text-xs italic text-muted-foreground mt-1 border-l-2 border-muted-foreground/30 pl-2">"{f.quote}"</div>}
                                            <div className="text-sm mt-2"><b>Issue:</b> {f.why_problematic}</div>
                                            {f.suggested_redraft && <div className="text-xs mt-1"><b>Redraft:</b> {f.suggested_redraft}</div>}
                                        </div>
                                    ))}
                                </div>
                            )}
                            {missing.length > 0 && (
                                <div className="space-y-2">
                                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Missing clauses</div>
                                    {missing.map((m, i) => (
                                        <div key={i} className="border border-amber-200 bg-amber-50/40 rounded-sm p-2" data-testid={`redflag-missing-${i}`}>
                                            <div className="text-sm font-medium">{m.clause}</div>
                                            <div className="text-xs text-muted-foreground">{m.why_needed}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
